import type { ConfigBundle } from '@cmp/contracts';
import type { Draft } from '../../../lib/types';
import { langLabel } from '../../../lib/labels';
import { t } from '../../../lib/i18n';

/**
 * Nudges that are not hard errors (Doc 04 §5.5).
 *
 * The server refuses what is actually unpublishable. These are the things that are
 * technically allowed and still get a banner challenged, so they belong in front of the
 * person choosing them rather than in a rejection afterwards.
 */
export interface Nudge {
  level: 'warn' | 'info';
  text: string;
}

export function nudges(draft: Draft, bundle: ConfigBundle | null = null): Nudge[] {
  const out: Nudge[] = [];
  const ui = (draft.ui ?? {}) as Record<string, any>;
  const buttons = (ui['buttons'] ?? {}) as Record<string, any>;

  if (buttons['settings']?.show === false) {
    out.push({
      level: 'warn',
      text: t('Ohne „Einstellungen“ gibt es keinen Weg zur granularen Auswahl — Einwilligung muss granular möglich sein.'),
    });
  }
  if (buttons['reject_all']?.style === 'link' && buttons['accept_all']?.style === 'primary') {
    out.push({
      level: 'warn',
      text: t('Ablehnen als Link neben Akzeptieren als Button ist der klassische Dark-Pattern-Vorwurf.'),
    });
  }
  // Order is a compliance surface, not decoration: DOM order is reading order and tab
  // order (WCAG 2.4.3), so it decides what a screen reader announces first and what the
  // keyboard reaches first. Accept before reject is allowed and is also the arrangement
  // supervisory authorities look at hardest — worth saying before publishing, not after.
  const order: string[] = ui['buttons']?.order ?? [];
  const accept = order.indexOf('accept_all');
  const reject = order.indexOf('reject_all');
  if (accept !== -1 && reject !== -1 && accept < reject) {
    out.push({
      level: 'warn',
      text: t('Akzeptieren steht vor Ablehnen. Screenreader und Tastatur erreichen damit die Zustimmung zuerst — erlaubt, aber die Anordnung, die am ehesten beanstandet wird.'),
    });
  }

  if (ui['reopen']?.mode === 'none') {
    out.push({
      level: 'warn',
      text: t('Ohne Badge oder eigenen Link ist der Widerruf nicht so einfach wie die Einwilligung.'),
    });
  }
  const optional = draft.categories.filter((c) => !c.required);
  if (optional.length > 0 && optional.every((c) => c.default)) {
    out.push({
      level: 'warn',
      text: t('Alle optionalen Kategorien sind vorausgewählt. Unter Opt-in ist das keine freiwillige Einwilligung.'),
    });
  }
  // Naming recipients is a hard Art. 13 duty, and the switch for it sits three clicks
  // deep under "Auswahl-Dialog" — so a site can carry a full, scanned vendor list and
  // still show a dialog that names nobody, with nothing anywhere saying so. The nudge is
  // the only thing standing between "we maintain vendors" and "our visitors ever see
  // one". Effective value, not draft-only: the switch may have been set in a previous
  // publication and left untouched in this draft.
  const vendors = bundle?.vendors ?? [];
  const showVendors = ui['prefs']?.show_vendors ?? (bundle?.ui as any)?.prefs?.show_vendors;
  if (vendors.length > 0 && showVendors !== true) {
    out.push({
      level: 'warn',
      text: t('{n} Anbieter sind gepflegt, aber der Dialog nennt keinen davon. Art. 13 verlangt, dass Empfänger benannt werden — einzuschalten unter „Auswahl-Dialog“.', { n: vendors.length }),
    });
  }

  if (draft.i18n_available.length > 1) {
    const missing = draft.i18n_available.filter((l) => !draft.texts[l]?.title || !draft.texts[l]?.body);
    if (missing.length) {
      out.push({
        level: 'info',
        text: t('Ohne Übersetzung: {langs} — Besucher mit dieser Spracheinstellung sehen den deutschen Text.',
          { langs: missing.map(langLabel).join(', ') }),
      });
    }
  }
  return out;
}
